import { useSceneObjectTypes } from "../SceneObjectTypesContext";
import type {
  LayoutListTargetOption,
  LayoutListTargetValue,
  ListValue,
  LocationValue,
  NodePortValue,
  Port,
  TransformValue,
  VariableValueType,
  VariableNode as VariableNodeType,
} from "../../../../util/src/blueprint/types.js";
import { Menu, MenuItem, TextField } from "@mui/material";
import { getValueTypeColor } from "../../../../util/src/blueprint/method.js";
import { PortHandle } from "../PortHandle.js";
import dico from "../../../../util/src/language/dico.js";
import React, { useMemo, useState } from "react";
import type { MouseEvent } from "react";
import type { BorderConfig } from "../../components/BorderPicker.js";
import type { BorderRadiusConfig } from "../../components/BorderRadiusPicker.js";
import type { SpacingConfig } from "../../components/SpacingPicker.js";
import {
  CUSTOM_DISCIPLINE_ATHLETE_SOURCE,
  CUSTOM_DISCIPLINE_ATHLETES_SOURCE,
  CUSTOM_DISCIPLINE_GROUP_SOURCE,
  CUSTOM_DISCIPLINE_INPUT_VALUES_SOURCE,
  CUSTOM_DISCIPLINE_MODAL_VALUES_SOURCE,
  CUSTOM_DISCIPLINE_REFERENCE_ITEM_SOURCE,
  CUSTOM_DISCIPLINE_SELECTED_COLUMN_KEY_SOURCE,
  CUSTOM_DISCIPLINE_SELECTED_ROW_ID_SOURCE,
} from "../../../../util/src/custom-discipline-behavior/sources.js";

const LAYOUT_LIST_ROW_ITEM_OBJECT_SOURCE = "layoutListRowItem";
const LAYOUT_LIST_COMPARATOR_OBJECT_SOURCE = "layoutListComparator";

export interface VariableNodeProps {
  node: VariableNodeType;
  setPortsMap: (
    nodeId: string,
    portId: string,
    element: HTMLElement | null,
  ) => void;
  onPortMouseDown?: (nodeId: string, port: Port) => void;
  onPortMouseUp?: (nodeId: string, port: Port) => void;
  isPortConnected?: (nodeId: string, portId: string) => boolean;
  onPortValueChange?: (
    nodeId: string,
    portId: string,
    value:
      | string
      | number
      | boolean
      | ListValue
      | NodePortValue
      | LayoutListTargetValue
      | LocationValue
      | TransformValue
      | BorderConfig
      | BorderRadiusConfig
      | SpacingConfig,
  ) => void;
  onToggleExpandedPorts?: (nodeId: string) => void;
  onListItemValueTypeChange?: (
    nodeId: string,
    valueType: Exclude<VariableValueType, "list">,
  ) => void;
  layoutListTargetOptions?: LayoutListTargetOption[];
}

const VariableNodeComponent = ({
  node,
  setPortsMap,
  onPortMouseDown,
  onPortMouseUp,
  isPortConnected,
  onPortValueChange,
  onToggleExpandedPorts,
  onListItemValueTypeChange,
  layoutListTargetOptions,
}: VariableNodeProps) => {
  const behaviorDico = dico().behavior;
  const objectCatalog = useSceneObjectTypes();
  const [portMenuState, setPortMenuState] = useState<{
    mouseX: number;
    mouseY: number;
    target: "value" | "split";
  } | null>(null);
  const layoutDataSourcesDico = dico().licensedPortal.layoutEditor
    .dataSources as Record<string, string>;
  const customDisciplineBehaviorVariablesDico = dico().admin.customDisciplines
    .behaviorVariables as Record<string, string>;
  const outputPort = node.ports.find((port) => port.direction === "output");
  const comparatorPortA = node.ports.find((port) => port.id === "outputA");
  const comparatorPortB = node.ports.find((port) => port.id === "outputB");
  const isObjectVariable = node.valueType === "object" && !!node.objectSource;
  const isScalarSourceVariable =
    node.valueType !== "object" &&
    !!node.objectSource &&
    !(node.valueType === "node" && node.objectSource === "barLoaderDisks");
  const isBarLoaderNodeVariable =
    node.valueType === "node" && node.objectSource === "barLoaderDisks";
  const isLayoutListRowItemVariable =
    node.valueType === "object" &&
    node.objectSource === LAYOUT_LIST_ROW_ITEM_OBJECT_SOURCE;
  const isLayoutListComparatorVariable =
    node.valueType === "object" &&
    node.objectSource === LAYOUT_LIST_COMPARATOR_OBJECT_SOURCE;
  const extensionTypeId = outputPort?.objectTypeId;
  const extensionLabel = extensionTypeId?.startsWith("source:")
    ? (
        behaviorDico.sceneCatalog.objectEditor.nativeTypes as Record<
          string,
          string
        >
      )[extensionTypeId.slice(7)]
    : objectCatalog?.objectTypes?.find((type) => type.id === extensionTypeId)
        ?.name;
  const title = isLayoutListRowItemVariable
    ? behaviorDico.actions.currentRow
    : isLayoutListComparatorVariable
      ? behaviorDico.actions.comparator
      : isObjectVariable || isScalarSourceVariable
        ? node.objectSource?.startsWith("@objectBase:")
          ? (extensionLabel ??
            layoutDataSourcesDico[outputPort?.label ?? ""] ??
            outputPort?.label ??
            "")
          : (layoutDataSourcesDico[node.objectSource as string] ??
            (node.objectSource === CUSTOM_DISCIPLINE_ATHLETE_SOURCE
              ? customDisciplineBehaviorVariablesDico.athlete
              : node.objectSource === CUSTOM_DISCIPLINE_ATHLETES_SOURCE
                ? customDisciplineBehaviorVariablesDico.athletes
                : node.objectSource === CUSTOM_DISCIPLINE_GROUP_SOURCE
                  ? customDisciplineBehaviorVariablesDico.group
                  : node.objectSource ===
                      CUSTOM_DISCIPLINE_SELECTED_ROW_ID_SOURCE
                    ? customDisciplineBehaviorVariablesDico.selectedRowId
                    : node.objectSource ===
                        CUSTOM_DISCIPLINE_SELECTED_COLUMN_KEY_SOURCE
                      ? customDisciplineBehaviorVariablesDico.selectedColumnKey
                      : node.objectSource ===
                          CUSTOM_DISCIPLINE_MODAL_VALUES_SOURCE
                        ? customDisciplineBehaviorVariablesDico.modalValues
                        : node.objectSource ===
                            CUSTOM_DISCIPLINE_INPUT_VALUES_SOURCE
                          ? customDisciplineBehaviorVariablesDico.inputValues
                          : node.objectSource ===
                              CUSTOM_DISCIPLINE_REFERENCE_ITEM_SOURCE
                            ? customDisciplineBehaviorVariablesDico.referenceItem
                            : undefined) ??
            node.objectSource)
        : isBarLoaderNodeVariable
          ? behaviorDico.actions.barLoaderDisks
          : (behaviorDico.nodeList.inputTypes[
              node.valueType as keyof typeof behaviorDico.nodeList.inputTypes
            ] ?? node.valueType);
  const objectColor = getValueTypeColor("object");
  const isCompositeLiteral =
    !node.objectSource &&
    (node.valueType === "location" || node.valueType === "transform");
  const splitPortIds = useMemo(
    () =>
      node.valueType === "location"
        ? ["output.x", "output.y", "output.z"]
        : ["output.width", "output.height", "output.rotation"],
    [node.valueType],
  );
  const splitPorts = splitPortIds
    .map((portId) => node.ports.find((port) => port.id === portId))
    .filter((port): port is Port => Boolean(port));
  const isExpandedComposite = isCompositeLiteral && splitPorts.length === 3;
  const collapsedCompositeValue =
    outputPort?.value && typeof outputPort.value === "object"
      ? (outputPort.value as Partial<LocationValue & TransformValue>)
      : {};
  const getCompositeNumber = (value: unknown): number => {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    const next = Number(value);
    return Number.isFinite(next) ? next : 0;
  };

  const handleCompositePortContextMenu = (
    event: MouseEvent,
    target: "value" | "split",
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setPortMenuState({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
      target,
    });
  };

  const typeColor = getValueTypeColor(
    isScalarSourceVariable ? node.valueType : "object",
  );
  if (!outputPort && !isLayoutListComparatorVariable) {
    return null;
  }

  return (
    <div
      style={{
        borderRadius: 8,
        overflow: "hidden",
        background:
          "linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(200, 200, 200, 0.98) 100%)",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        border: isScalarSourceVariable
          ? `1px solid ${typeColor}50`
          : isObjectVariable
            ? `1px solid ${objectColor}50`
            : "1px solid #e5e7eb",
        minWidth: isBarLoaderNodeVariable ? 300 : "unset",
      }}
    >
      {/* Source-bound variable (scalar/object) - compact row: source name + port */}
      {outputPort &&
        (isScalarSourceVariable ||
          (isObjectVariable && !isLayoutListComparatorVariable)) && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "2px 4px 2px 8px",
            }}
          >
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: isObjectVariable ? objectColor : typeColor,
                whiteSpace: "nowrap",
              }}
            >
              {title}
            </span>
            <PortHandle
              node={node}
              port={outputPort}
              setPortsMap={setPortsMap}
              onMouseDown={() => onPortMouseDown?.(node.id, outputPort)}
              onMouseUp={() => onPortMouseUp?.(node.id, outputPort)}
              isConnected={isPortConnected?.(node.id, outputPort.id) ?? false}
            />
          </div>
        )}
      {isLayoutListComparatorVariable && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "2px 4px 2px 8px",
            gap: 8,
          }}
        >
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: objectColor,
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </span>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 2,
            }}
          >
            {comparatorPortA && (
              <PortHandle
                node={node}
                port={comparatorPortA}
                setPortsMap={setPortsMap}
                onMouseDown={() => onPortMouseDown?.(node.id, comparatorPortA)}
                onMouseUp={() => onPortMouseUp?.(node.id, comparatorPortA)}
                isConnected={
                  isPortConnected?.(node.id, comparatorPortA.id) ?? false
                }
              />
            )}
            {comparatorPortB && (
              <PortHandle
                node={node}
                port={comparatorPortB}
                setPortsMap={setPortsMap}
                onMouseDown={() => onPortMouseDown?.(node.id, comparatorPortB)}
                onMouseUp={() => onPortMouseUp?.(node.id, comparatorPortB)}
                isConnected={
                  isPortConnected?.(node.id, comparatorPortB.id) ?? false
                }
              />
            )}
          </div>
        </div>
      )}
      {isCompositeLiteral && outputPort && (
        <div
          style={{
            padding: "8px",
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
          onMouseDown={(event) => {
            const target = event.target as HTMLElement;
            const shouldBlockDrag =
              Boolean(target.closest("[data-port-handle]")) ||
              Boolean(target.closest(".MuiInputBase-root")) ||
              target.tagName.toLowerCase() === "input" ||
              target.tagName.toLowerCase() === "textarea" ||
              target.tagName.toLowerCase() === "select";
            if (shouldBlockDrag) {
              event.stopPropagation();
            }
          }}
        >
          {!isExpandedComposite ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "100%",
              }}
              onContextMenu={(event) =>
                handleCompositePortContextMenu(event, "value")
              }
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  flex: 1,
                }}
                onContextMenu={(event) =>
                  handleCompositePortContextMenu(event, "value")
                }
              >
                {node.valueType === "location" ? (
                  <>
                    <TextField
                      size="small"
                      type="number"
                      value={getCompositeNumber(collapsedCompositeValue.x)}
                      onMouseDown={(event) => event.stopPropagation()}
                      onChange={(event) =>
                        onPortValueChange?.(node.id, outputPort.id, {
                          x: Number(event.target.value),
                          y: getCompositeNumber(collapsedCompositeValue.y),
                          z: getCompositeNumber(collapsedCompositeValue.z),
                        })
                      }
                      sx={{
                        width: 68,
                        "& .MuiInputBase-input": {
                          py: 0.4,
                          px: 0.9,
                          fontSize: 12,
                        },
                      }}
                    />
                    <TextField
                      size="small"
                      type="number"
                      value={getCompositeNumber(collapsedCompositeValue.y)}
                      onMouseDown={(event) => event.stopPropagation()}
                      onChange={(event) =>
                        onPortValueChange?.(node.id, outputPort.id, {
                          x: getCompositeNumber(collapsedCompositeValue.x),
                          y: Number(event.target.value),
                          z: getCompositeNumber(collapsedCompositeValue.z),
                        })
                      }
                      sx={{
                        width: 68,
                        "& .MuiInputBase-input": {
                          py: 0.4,
                          px: 0.9,
                          fontSize: 12,
                        },
                      }}
                    />
                    <TextField
                      size="small"
                      type="number"
                      value={getCompositeNumber(collapsedCompositeValue.z)}
                      onMouseDown={(event) => event.stopPropagation()}
                      onChange={(event) =>
                        onPortValueChange?.(node.id, outputPort.id, {
                          x: getCompositeNumber(collapsedCompositeValue.x),
                          y: getCompositeNumber(collapsedCompositeValue.y),
                          z: Number(event.target.value),
                        })
                      }
                      sx={{
                        width: 68,
                        "& .MuiInputBase-input": {
                          py: 0.4,
                          px: 0.9,
                          fontSize: 12,
                        },
                      }}
                    />
                  </>
                ) : (
                  <>
                    <TextField
                      size="small"
                      type="number"
                      value={getCompositeNumber(collapsedCompositeValue.width)}
                      onMouseDown={(event) => event.stopPropagation()}
                      onChange={(event) =>
                        onPortValueChange?.(node.id, outputPort.id, {
                          width: Number(event.target.value),
                          height: getCompositeNumber(
                            collapsedCompositeValue.height,
                          ),
                          rotation: getCompositeNumber(
                            collapsedCompositeValue.rotation,
                          ),
                        })
                      }
                      sx={{
                        width: 68,
                        "& .MuiInputBase-input": {
                          py: 0.4,
                          px: 0.9,
                          fontSize: 12,
                        },
                      }}
                    />
                    <TextField
                      size="small"
                      type="number"
                      value={getCompositeNumber(collapsedCompositeValue.height)}
                      onMouseDown={(event) => event.stopPropagation()}
                      onChange={(event) =>
                        onPortValueChange?.(node.id, outputPort.id, {
                          width: getCompositeNumber(
                            collapsedCompositeValue.width,
                          ),
                          height: Number(event.target.value),
                          rotation: getCompositeNumber(
                            collapsedCompositeValue.rotation,
                          ),
                        })
                      }
                      sx={{
                        width: 68,
                        "& .MuiInputBase-input": {
                          py: 0.4,
                          px: 0.9,
                          fontSize: 12,
                        },
                      }}
                    />
                    <TextField
                      size="small"
                      type="number"
                      value={getCompositeNumber(
                        collapsedCompositeValue.rotation,
                      )}
                      onMouseDown={(event) => event.stopPropagation()}
                      onChange={(event) =>
                        onPortValueChange?.(node.id, outputPort.id, {
                          width: getCompositeNumber(
                            collapsedCompositeValue.width,
                          ),
                          height: getCompositeNumber(
                            collapsedCompositeValue.height,
                          ),
                          rotation: Number(event.target.value),
                        })
                      }
                      sx={{
                        width: 68,
                        "& .MuiInputBase-input": {
                          py: 0.4,
                          px: 0.9,
                          fontSize: 12,
                        },
                      }}
                    />
                  </>
                )}
              </div>
              <PortHandle
                node={node}
                port={outputPort}
                setPortsMap={setPortsMap}
                onMouseDown={() => onPortMouseDown?.(node.id, outputPort)}
                onMouseUp={() => onPortMouseUp?.(node.id, outputPort)}
                isConnected={isPortConnected?.(node.id, outputPort.id) ?? false}
                onContextMenu={(event) =>
                  handleCompositePortContextMenu(event, "value")
                }
              />
            </div>
          ) : (
            splitPorts.map((port) => (
              <div
                key={port.id}
                style={{ width: "100%" }}
                onContextMenu={(event) =>
                  handleCompositePortContextMenu(event, "split")
                }
              >
                <PortHandle
                  node={node}
                  port={port}
                  setPortsMap={setPortsMap}
                  onMouseDown={() => onPortMouseDown?.(node.id, port)}
                  onMouseUp={() => onPortMouseUp?.(node.id, port)}
                  showValueInput
                  isConnected={isPortConnected?.(node.id, port.id) ?? false}
                  onValueChange={(value) =>
                    onPortValueChange?.(
                      node.id,
                      port.id,
                      typeof value === "number" ? value : Number(value),
                    )
                  }
                  onContextMenu={(event) =>
                    handleCompositePortContextMenu(event, "split")
                  }
                />
              </div>
            ))
          )}
        </div>
      )}
      {isBarLoaderNodeVariable && outputPort && (
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 14,
              padding: "3px 8px",
              fontWeight: 700,
              color: "#f8f5ff",
              letterSpacing: 0.35,
              border: `1px solid ${objectColor}`,
              background: `linear-gradient(180deg, #b59bff 0%, ${objectColor} 52%, #5a36a5 100%)`,
            }}
          >
            {title}
          </div>
          <div
            style={{
              padding: "4px 8px 4px",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 8,
              width: "100%",
              boxSizing: "border-box",
            }}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div style={{ flex: 1, minWidth: 0, display: "flex", gap: 8 }}>
              <TextField
                select
                size="small"
                value={(
                  (outputPort.value as NodePortValue | undefined)?.weight ??
                  node.nodeWeights?.[0] ??
                  25
                ).toString()}
                onChange={(event) => {
                  const rawWeight = event.target.value;
                  const nextWeight =
                    rawWeight === "all" ? "all" : Number(rawWeight);
                  const current = (outputPort.value as
                    | NodePortValue
                    | undefined) ?? {
                    component: "barLoader",
                    weight: nextWeight,
                    part: "disk",
                  };
                  onPortValueChange?.(node.id, outputPort.id, {
                    ...current,
                    component: "barLoader",
                    weight: nextWeight,
                  });
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-input": { py: 0.45, fontSize: 12 },
                }}
              >
                <MenuItem value="all">
                  {behaviorDico.actions.barLoaderTargetAll}
                </MenuItem>
                {(node.nodeWeights ?? []).map((weight) => (
                  <MenuItem key={weight} value={weight.toString()}>
                    {weight}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                value={
                  (outputPort.value as NodePortValue | undefined)?.part ??
                  "disk"
                }
                onChange={(event) => {
                  const nextPart = event.target.value as "disk" | "label";
                  const current = (outputPort.value as
                    | NodePortValue
                    | undefined) ?? {
                    component: "barLoader",
                    weight: node.nodeWeights?.[0] ?? 25,
                    part: nextPart,
                  };
                  onPortValueChange?.(node.id, outputPort.id, {
                    ...current,
                    component: "barLoader",
                    part: nextPart,
                  });
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-input": { py: 0.45, fontSize: 12 },
                }}
              >
                <MenuItem value="disk">
                  {behaviorDico.actions.barLoaderTargetDisk}
                </MenuItem>
                <MenuItem value="label">
                  {behaviorDico.actions.barLoaderTargetLabel}
                </MenuItem>
              </TextField>
            </div>
            <div style={{ flexShrink: 0 }}>
              <PortHandle
                node={node}
                port={outputPort}
                setPortsMap={setPortsMap}
                onMouseDown={() => onPortMouseDown?.(node.id, outputPort)}
                onMouseUp={() => onPortMouseUp?.(node.id, outputPort)}
                isConnected={isPortConnected?.(node.id, outputPort.id) ?? false}
              />
            </div>
          </div>
        </div>
      )}
      {outputPort &&
        !isObjectVariable &&
        !isScalarSourceVariable &&
        !isCompositeLiteral &&
        !isBarLoaderNodeVariable && (
          <PortHandle
            node={node}
            port={outputPort}
            setPortsMap={setPortsMap}
            onMouseDown={() => onPortMouseDown?.(node.id, outputPort)}
            onMouseUp={() => onPortMouseUp?.(node.id, outputPort)}
            showValueInput={!isObjectVariable}
            isConnected={isPortConnected?.(node.id, outputPort.id) ?? false}
            onValueChange={(value) =>
              onPortValueChange?.(node.id, outputPort.id, value)
            }
            onListItemValueTypeChange={onListItemValueTypeChange}
            layoutListTargetOptions={layoutListTargetOptions}
          />
        )}
      <Menu
        open={portMenuState != null}
        onClose={() => setPortMenuState(null)}
        anchorReference="anchorPosition"
        anchorPosition={
          portMenuState != null
            ? { top: portMenuState.mouseY, left: portMenuState.mouseX }
            : undefined
        }
      >
        {portMenuState?.target === "value" ? (
          <MenuItem
            onClick={() => {
              onToggleExpandedPorts?.(node.id);
              setPortMenuState(null);
            }}
          >
            {behaviorDico.actions.splitPorts}
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              onToggleExpandedPorts?.(node.id);
              setPortMenuState(null);
            }}
          >
            {behaviorDico.actions.joinPorts}
          </MenuItem>
        )}
      </Menu>
    </div>
  );
};

export const VariableNode = React.memo(VariableNodeComponent);
